import { MockDatabase, MockRecord, MockTable, TableQuery } from "../MockDatabase";

export type LinkAPI = {
    organization_guid: string;
    link_guid: string;
    display_name: string;
    bundle_guid: string | null;
    bundle_display_name: string | null;
    route_name: string | null;
    url: string;
    product: string | null;
    created_date: string;
    updated_date: string;
};

export type LinkListAPI = Omit<LinkAPI, 'organization_guid'>;

export class MockLinks extends MockTable {
    constructor(db: MockDatabase) {
        super(db);
        this.name = 'links';
        this.fields = {
            id: this.PrimaryKey({
                fieldName: 'id'
            }),
            link_guid: this.PublicKey({
                fieldName: 'link_guid'
            }),
            display_name: this.Field({
                type: 'string',
                fieldName: 'display_name',
                nullable: false
            }),
            organization_id: this.ForeignKey({
                fieldName: 'organization_id',
                nullable: false,
                index: true,
                foreignTableName: 'organizations',
                foreignTableFieldName: 'id',
            }),
            product: this.Field({
                type: 'string',
                fieldName: 'product',
                nullable: true
            }),
            bundle_id: this.ForeignKey({
                fieldName: 'bundle_id',
                nullable: true,
                index: true,
                foreignTableName: 'bundles',
                foreignTableFieldName: 'id',
            }),
            route_name: this.Field({
                type: 'string',
                fieldName: 'route_name',
                nullable: true,
                index: true,
            }),
            created_date: this.Field({
                type: 'datetime',
                fieldName: 'created_date',
                nullable: false,
                default: {
                    method: 'NOW'
                }
            }),
            updated_date: this.Field({
                type: 'datetime',
                fieldName: 'updated_date',
                nullable: false,
                default: {
                    method: 'NOW',
                    onUpdate: true
                }
            }),
        }
    }

    private _transformPayload(db: MockDatabase, values: Record<string, any>) {
        let {
            organization_guid, organizationGuid, organization_id, 
            bundle_guid, bundleGuid, bundle_id, 
            ...data
        } = values;
        if ((organization_guid || organizationGuid) && !organization_id) {
            organization_id = db.resolveGuidToId('organizations', (organization_guid || organizationGuid));
        }
        if ((bundle_guid || bundleGuid) && !bundle_id) {
            bundle_id = db.resolveGuidToId('bundles', (bundle_guid || bundleGuid));
        }
        return {
            ...data,
            ...(bundle_id ? {bundle_id} : {}),
            ...(organization_id ? {organization_id} : {}),
        }
    }
    
    protected beforeQuery: (db: MockDatabase, table: MockTable, query: TableQuery) => TableQuery = (db, table, query) => {
        const transFormFields: Record<string, any> = {};
        const remQuery: TableQuery = [];
        query.forEach(entry => {
            if (entry.fieldName === 'organization_guid' || entry.fieldName === 'organizationGuid') {
                transFormFields['organization_guid'] = entry.value;
            } else if (entry.fieldName === 'bundle_guid' || entry.fieldName === 'bundleGuid') {
                transFormFields['bundle_guid'] = entry.value;
            } else {
                remQuery.push(entry);
            }
        });
        const fixed = this._transformPayload(db, transFormFields);
        Object.entries(fixed).forEach(([fieldName, value]) => remQuery.unshift({
            fieldName,
            value
        }));
        return remQuery;
    }

    protected beforeCreate: (db: MockDatabase, table: MockTable, values: Record<string, any>) => Record<string, any> = (db, table, values) => {
        return this._transformPayload(db, values);
    }

    protected beforeUpdate: (db: MockDatabase, table: MockTable, records: MockRecord[], data: Record<string, any>) => Record<string, any> = (db, table, records, data) => {
        return this._transformPayload(db, data);
    }
    
    serialize = (record: MockRecord) => {
        const bundles = record.getRelated('bundles');
        return {
            organization_guid: record.values['organization_guid'],
            link_guid: record.values['link_guid'],
            display_name: record.values['display_name'],
            bundle_guid: bundles.length ? bundles[0].values['bundle_guid'] : undefined,
            bundle_display_name: record.values['bundle_display_name'],
            route_name: record.values['route_name'],
            url: record.values['url'],
            product: record.values['product'],
            created_date: record.values['created_date'],
            updated_date: record.values['updated_date'],
        } as LinkAPI
    }
    
    serializeList = (records: MockRecord[]) => records.map(record => {
        const {organization_guid, ...link} = this.serialize(record);
        return link;
    }) as LinkListAPI[];
}